import { css } from '@emotion/react';
import { Add, Remove } from '@mui/icons-material';
import { Box, Button, FormHelperText, IconButton, MenuItem } from '@mui/material';
import { FieldArray, FormikProps } from 'formik';
import { DateTime } from 'luxon';
import { isEmpty, pick } from 'ramda';
import { Fragment, useMemo } from 'react';

import { parseDate } from '@jebel/utils';

import { useCurrentUserCommunityMembership } from 'features/community/hooks';
import { CheckboxField } from 'shared/components/form';
import { SettingsContentLayout } from 'shared/components/layouts';
import {
  Form,
  FormTextField,
  FormSelect,
  FormPhoneFieldV2 as FormPhoneField,
  FormKeyboardDatePicker,
  AutocompleteField,
} from 'shared/components/ui';
import { US_STATES_OPTIONS, FIELDS_CONFIG_USER } from 'shared/constants';
import {
  UserUpdateInput,
  UsersUserPreferencesUpdateRelationInput,
  useUserUpdateMutation,
} from 'shared/graphql';
import { useCurrentUser, useResponsive, useToast } from 'shared/hooks';
import { required } from 'shared/utils/form';
import { recordError } from 'shared/utils/record';

const formCSS = css`
  display: grid;
  grid-gap: 20px;
`;

const rowLayoutCss = (isMobile: boolean) => css`
  display: grid;
  grid-gap: 20px;
  ${isMobile ? `grid-template-rows: 1fr 1fr;` : `grid-template-columns: 1fr 1fr;`}
`;

type Parent = {
  fullName: string;
  classYear?: string | null;
};

type FormData = {
  firstName?: string;
  lastName?: string;
  gender?: string;
  affiliation?: string;
  affiliationDescription?: string;
  graduatingYear?: string;
  birthDate?: DateTime | null;
  parents?: Array<Parent>;
  description?: string;
  isParentsAvailable?: boolean;
  userPreferences?: {
    phoneNumber?: string;
    secondaryPhoneNumber?: string;
    address?: {
      zip?: string | null;
      city?: string | null;
      state?: string | null;
    };
  };
};

const UPDATING_MESSAGE_KEY = 'UPDATING_MESSAGE_KEY';

export function SettingsAccountInformationPage() {
  const { isMobile } = useResponsive();
  const { user, loading } = useCurrentUser();
  const { data: membership } = useCurrentUserCommunityMembership();
  const { showError, showMessage, showSuccess, dismiss } = useToast();

  const [updateUser] = useUserUpdateMutation({
    refetchQueries: ['CurrentUser', 'UserPublicProfile'],
    awaitRefetchQueries: true,
  });

  const initials = useMemo<FormData>(() => {
    const picked = pick(
      [
        'firstName',
        'lastName',
        'gender',
        'affiliation',
        'graduatingYear',
        'birthDate',
        'parents',
        'description',
        'isParentsAvailable',
        'userPreferences',
      ],
      user as NonNullable<typeof user>,
    );

    const initialParents = [
      {
        fullName: '',
        classYear: '',
      },
    ];

    const birthDate = parseDate(user?.birthDate);

    const payload: FormData = {
      firstName: picked?.firstName ?? undefined,
      lastName: picked?.lastName ?? undefined,
      gender: picked?.gender ?? undefined,
      affiliation: picked?.affiliation ?? undefined,
      graduatingYear: picked?.graduatingYear ?? undefined,
      birthDate: null,

      parents: isEmpty(picked?.parents || []) ? initialParents : picked?.parents,
      isParentsAvailable: !isEmpty(picked?.parents),

      userPreferences: {
        phoneNumber: picked?.userPreferences?.phoneNumber ?? '',
        secondaryPhoneNumber: picked?.userPreferences?.secondaryPhoneNumber ?? '',
        address: {
          zip: picked?.userPreferences?.address?.zip ?? '',
          city: picked?.userPreferences?.address?.city ?? '',
          state: picked?.userPreferences?.address?.state ?? '',
        },
      },
    };

    if (birthDate.isValid) {
      payload.birthDate = birthDate;
    }

    return payload;
  }, [user]);

  const handleSubmit = async (form: FormData) => {
    if (!user?.id) {
      return;
    }

    try {
      const birthDate = form.birthDate?.endOf('day');
      const parents = form.isParentsAvailable ? form.parents : [];

      const userPreferences: UsersUserPreferencesUpdateRelationInput = {
        [user?.userPreferences?.id ? 'update' : 'create']: {
          phoneNumber: form.userPreferences?.phoneNumber,
          secondaryPhoneNumber: form.userPreferences?.secondaryPhoneNumber,

          address: {
            [user?.userPreferences?.address?.id ? 'update' : 'create']:
              form.userPreferences?.address,
          },
        },
      };

      const data: UserUpdateInput = {
        id: user.id,
        firstName: form.firstName,
        lastName: form.lastName,
        gender: form.gender,
        graduatingYear: form.graduatingYear,

        // Save for compatibility with the old affiliation field.
        // https://github.com/jebelapp/jebel/issues/1841
        affiliation: form.affiliation,
        affiliationDescription: form.affiliationDescription,

        birthDate: null,
        userPreferences,
        parents,
      };

      if (membership) {
        data.schoolMemberships = {
          update: [
            {
              filter: { id: membership.id },
              data: {
                // Save in the new affiliation field.
                // https://github.com/jebelapp/jebel/issues/1841
                affiliation: form.affiliation,
                affiliationDescription: form.affiliationDescription,
              },
            },
          ],
        };
      }

      if (birthDate?.isValid) {
        data.birthDate = birthDate.toSQLDate();
      }

      showMessage('Updating your account information.', { id: UPDATING_MESSAGE_KEY });

      await updateUser({ variables: { data } });
      showSuccess('Account information was successfully updated');
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(UPDATING_MESSAGE_KEY);
    }
  };

  const onResetForm = (resetForm: FormikProps<FormData>['resetForm']) => () => {
    resetForm();
  };

  return (
    <SettingsContentLayout title="Account Information" isLoading={loading}>
      <Form<FormData>
        oldCss={formCSS}
        validateOnChange={false}
        initialValues={initials}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ isSubmitting, values, dirty, resetForm }) => (
          <Fragment>
            <Box css={rowLayoutCss(isMobile)}>
              <FormTextField
                inputProps={{
                  label: 'First Name',
                  variant: 'outlined',
                }}
                fieldProps={{ name: 'firstName', validate: required }}
              />

              <FormTextField
                inputProps={{
                  label: 'Last Name',
                  variant: 'outlined',
                }}
                fieldProps={{ name: 'lastName', validate: required }}
              />
            </Box>

            <FormHelperText>
              To add a NICK NAME or MIDDLE NAME use the First Name field: John “Nickname” or John
              Middle.
            </FormHelperText>

            <Box css={rowLayoutCss(isMobile)}>
              <FormPhoneField
                inputProps={{
                  label: 'Primary Phone Number',
                  variant: 'outlined',
                }}
                fieldProps={{ name: 'userPreferences.phoneNumber' }}
              />

              <FormPhoneField
                inputProps={{
                  label: 'Secondary Phone Number',
                }}
                fieldProps={{ name: 'userPreferences.secondaryPhoneNumber' }}
              />
            </Box>

            <Box css={rowLayoutCss(isMobile)}>
              <FormSelect
                selectProps={{
                  children: FIELDS_CONFIG_USER.gender.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  )),
                  variant: 'outlined',
                  label: 'Gender',
                }}
                fieldProps={{ name: 'gender', validate: required }}
              />
              <FormSelect
                selectProps={{
                  children: FIELDS_CONFIG_USER.affiliation.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  )),
                  variant: 'outlined',
                  label: 'Affiliation',
                }}
                fieldProps={{ name: 'affiliation', validate: required }}
              />

              {values.affiliation === 'alumni' && (
                <FormSelect
                  selectProps={{
                    children: FIELDS_CONFIG_USER.graduatingYear.map(item => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    )),
                    variant: 'outlined',
                    label: 'Graduation Year',
                  }}
                  fieldProps={{ name: 'graduatingYear', validate: required }}
                />
              )}

              {values.affiliation === 'other' && (
                <FormTextField
                  inputProps={{
                    label: 'Affiliation Description',
                    variant: 'outlined',
                  }}
                  fieldProps={{ name: 'affiliationDescription', validate: required }}
                />
              )}
            </Box>

            <CheckboxField name="isParentsAvailable" label="Parent (current student or alumni)?" />

            {values.isParentsAvailable && (
              <FieldArray
                name="parents"
                render={arrayHelpers => {
                  return (
                    <Fragment>
                      {values.parents?.map((parent, index: number) => (
                        <Box css={rowLayoutCss(isMobile)} key={index}>
                          <FormTextField
                            fieldProps={{
                              validate: required,
                              name: `parents.${index}.fullName`,
                            }}
                            inputProps={{
                              label: 'Full Name',
                              variant: 'outlined',
                            }}
                          />
                          <Box
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <FormSelect
                              selectProps={{
                                children: FIELDS_CONFIG_USER.graduatingYear.map(item => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                )),
                                variant: 'outlined',
                                label: 'Class Year',
                              }}
                              fieldProps={{
                                name: `parents.${index}.classYear`,
                                validate: required,
                              }}
                            />

                            <IconButton
                              disabled={index === 0}
                              onClick={() => arrayHelpers.remove(index)}
                              size="small"
                              style={{
                                height: 'min-content',
                              }}
                            >
                              <Remove />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}

                      <Button
                        variant="text"
                        color="secondary"
                        onClick={() => arrayHelpers.push('')}
                        startIcon={<Add />}
                      >
                        Add another
                      </Button>
                    </Fragment>
                  );
                }}
              />
            )}
            <Box css={rowLayoutCss(isMobile)}>
              <FormKeyboardDatePicker
                datePickerProps={{
                  allowKeyboardControl: true,
                  disableFuture: true,
                  inputVariant: 'outlined',
                  label: 'Date of birth',
                  variant: 'inline',
                }}
                fieldProps={{ name: 'birthDate' }}
              />
              <FormTextField
                inputProps={{
                  label: 'ZIP Code',
                  variant: 'outlined',
                }}
                fieldProps={{ name: 'userPreferences.address.zip' }}
              />
              <FormTextField
                inputProps={{
                  label: 'City',
                  variant: 'outlined',
                }}
                fieldProps={{ name: 'userPreferences.address.city' }}
              />

              <AutocompleteField
                name="userPreferences.address.state"
                label="State"
                options={US_STATES_OPTIONS}
              />
            </Box>

            <Box display="flex" justifyContent="end" gap="1rem">
              <Button
                disabled={!dirty || isSubmitting}
                variant="outlined"
                onClick={onResetForm(resetForm)}
              >
                Cancel
              </Button>

              <Button
                color="primary"
                disableElevation
                variant="contained"
                type="submit"
                disabled={!dirty || isSubmitting}
              >
                Save changes
              </Button>
            </Box>
          </Fragment>
        )}
      </Form>
    </SettingsContentLayout>
  );
}
